// ============================================
// ANTIMICROBIANOS — RDC ANVISA nº 471/2021 + IN ANVISA nº 360/2025 (B306 / B349)
// ============================================
// Substâncias que exigem RECEITA DE ANTIMICROBIANO: receita simples (branca) em
// 2 vias, validade 10 dias, retenção de uma via pela farmácia.
//
// ✅ FONTE OFICIAL, CONFERIDA (AI_151, 01/09/2026): a lista abaixo é a do **Art. 1º
//    da IN ANVISA nº 360/2025**, extraída do PDF oficial da norma, item a item.
//    Antes disso a lista era curada de memória (56 entradas) e tinha falhas nos dois
//    sentidos — ver B349 no backlog.
//
// ⚠️ ANOMALIAS DO TEXTO OFICIAL (registradas para quem for auditar):
// - A numeração da IN **pula o item 42** — são 131 substâncias, não 132.
// - A IN traz três grafias erradas, corrigidas aqui para o nome real (o comentário
//   de cada linha diz qual era): "capreomicin", "cefefpiroma", "ceftadizima".
// - A IN grafa as quinolonas no feminino ("ciprofloxacina"); o Brasil prescreve no
//   masculino ("Ciprofloxacino"). Resolvido pelo RADICAL (ver formato abaixo).
//
// 📌 FORA desta lista, por decisão registrada:
// - **RIFAXIMINA — NÃO consta da IN 360/2025** (conferido no PDF oficial: a família
//   rifamicínica listada é rifabutina, rifamicina, rifampicina e rifapentina). É uso
//   CRÔNICO na encefalopatia hepática; enquadrá-la imporia validade de 10 dias e
//   retenção a um tratamento contínuo. Decisão de Leonardo mantida na AI_151, agora
//   com a prova documental. **Não reabrir sem consultar a IN vigente.**
// - Os ANTIVIRAIS do HBV (entecavir, tenofovir/TDF/TAF) não são antimicrobianos da
//   RDC 471 e já têm fluxo próprio (Ficha de Hepatite B / SUS).
// - **cefixima, secnidazol e tinidazol** estavam na lista antiga e **não constam** da
//   IN 360 — removidos na AI_151 (disparavam receita de antimicrobiano sem a norma exigir).
//
// ⚖️ PENDENTE (gate do advogado/farmacêutico — B306):
// - A IN não exclui apresentações TÓPICAS/oftálmicas (bacitracina, mupirocina,
//   neomicina, gramicidina, retapamulina, besifloxacino, sulfacetamida...), então elas
//   disparam a receita de antimicrobiano. O médico pode desligar manualmente no
//   receituário se for falso positivo.
// - O **Art. 2º da IN 360** sujeita os análogos de GLP-1 (semaglutida, liraglutida,
//   dulaglutida, tirzepatida, lixisenatida) a prescrição com RETENÇÃO, mas com validade
//   de **90 dias** — regime diferente. NÃO tratado aqui; ver B350 no backlog.

// Formato: "nome" ou "nome|radical".
// - `nome`   = o que aparece para o médico no receituário;
// - `radical`= o que é procurado no texto do medicamento (cobre variações de grafia).
// A busca exige início de palavra, então "Ciprofloxacino" não casa com "ofloxacino".
const LISTA_IN360 = [
  "acido clavulanico", // 1 — SEM radical "clavulan" de propósito: o clavulanato
  // nunca é prescrito sozinho e, sendo o item 1, ganharia da amoxicilina na ordem
  // (a combinação apareceria como "acido clavulanico" em vez de "amoxicilina").
  "acido fusidico", // 2
  "acido nalidixico", // 3
  "acido oxolinico", // 4
  "acido pipemidico", // 5
  "amicacina", // 6
  "amoxicilina", // 7
  "ampicilina", // 8
  "axetilcefuroxima", // 9
  "azitromicina", // 10
  "aztreonam", // 11
  "bacitracina", // 12
  "besifloxacino|besifloxacin", // 13
  "brodimoprima", // 14
  "capreomicina|capreomicin", // 15 — IN grafa 'capreomicin'
  "carbenicilina", // 16
  "cefaclor", // 17
  "cefadroxil", // 18
  "cefalexina", // 19
  "cefalotina", // 20
  "cefazolina", // 21
  "cefepima", // 22
  "cefodizima", // 23
  "cefoperazona", // 24
  "cefotaxima", // 25
  "cefoxitina", // 26
  "cefpodoxima", // 27
  "cefpiroma", // 28 — IN grafa 'cefefpiroma'
  "cefprozil", // 29
  "ceftazidima", // 30 — IN grafa 'ceftadizima'
  "ceftarolina fosamila", // 31
  "ceftobiprol", // 32
  "ceftriaxona", // 33
  "cefuroxima", // 34
  "ciprofloxacino|ciprofloxacin", // 35 — IN grafa com -a; radical cobre as duas
  "claritromicina", // 36
  "clindamicina", // 37
  "clofazimina", // 38
  "clorfenesina", // 39
  "cloranfenicol", // 40
  "cloxacilina", // 41
  "dactinomicina", // 43
  "daptomicina", // 44
  "dapsona", // 45
  "delamanide", // 46
  "dicloxacilina", // 47
  "difenilsulfona", // 48
  "diidroestreptomicina", // 49
  "diritromicina", // 50
  "doripenem", // 51
  "doxiciclina", // 52
  "eritromicina", // 53
  "ertapenem", // 54
  "espectinomicina", // 55
  "espiramicina", // 56
  "estreptomicina", // 57
  "etambutol", // 58
  "etionamida", // 59
  "fosfomicina", // 60
  "ftalilsulfatiazol", // 61
  "gatifloxacino|gatifloxacin", // 62 — IN grafa com -a
  "gemifloxacino|gemifloxacin", // 63
  "gentamicina", // 64
  "gramicidina", // 65
  "imipenem", // 66
  "isoniazida", // 67
  "levofloxacino|levofloxacin", // 68 — IN grafa com -a
  "linezolida", // 69
  "limeciclina", // 70
  "lincomicina", // 71
  "lomefloxacino|lomefloxacin", // 72 — IN grafa com -a
  "loracarbef", // 73
  "mandelamina", // 74
  "meropenem", // 75
  "metampicilina", // 76
  "metronidazol", // 77
  "minociclina", // 78
  "miocamicina", // 79
  "mitomicina", // 80
  "moxifloxacino|moxifloxacin", // 81
  "mupirocina", // 82
  "neomicina", // 83
  "netilmicina", // 84
  "nitrofural", // 85
  "nitrofurantoina", // 86
  "nitroxolina", // 87
  "norfloxacino|norfloxacin", // 88 — IN grafa com -a
  "ofloxacino|ofloxacin", // 89 — IN grafa com -a
  "oxacilina", // 90
  "oxitetraciclina", // 91
  "pefloxacino|pefloxacin", // 92 — IN grafa com -a
  "penicilina g", // 93
  "penicilina v", // 94
  "piperacilina", // 95
  "pirazinamida", // 96
  "polimixina b|polimixina", // 97
  "pristinamicina", // 98
  "protionamida", // 99
  "retapamulina", // 100
  "rifabutina", // 101
  "rifamicina", // 102
  "rifampicina", // 103
  "rifapentina", // 104
  "rosoxacina", // 105
  "roxitromicina", // 106
  "sulbactam", // 107
  "sulfacetamida", // 108
  "sulfadiazina", // 109
  "sulfadoxina", // 110
  "sulfaguanidina", // 111
  "sulfamerazina", // 112
  "sulfanilamida", // 113
  "sulfametizol", // 114
  "sulfametoxazol", // 115
  "sulfametoxipiridazina", // 116
  "sulfametoxipirimidina", // 117
  "sulfatiazol", // 118
  "sultamicilina", // 119
  "tazobactam", // 120
  "tedizolida", // 121
  "teicoplanina", // 122
  "telitromicina", // 123
  "tetraciclina", // 124
  "tianfenicol", // 125
  "ticarcilina", // 126
  "tigeciclina", // 127
  "tirotricina", // 128
  "tobramicina", // 129
  "trimetoprima", // 130
  "trovafloxacino|trovafloxacin", // 131 — IN grafa com -a
  "vancomicina", // 132
];

// Aliases operacionais: grafias correntes no Brasil que a IN não usa literalmente.
// (A IN lista apenas "penicilina G" e "penicilina V".)
const ALIASES = [
  "penicilina", // cobre "Penicilina Benzatina", "Penicilina Cristalina"
  "penicilina g|benzilpenicilina", // DCB da penicilina G
];

const normalizar = (s: string): string =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const escaparRegex = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

type Antimicrobiano = { nome: string; re: RegExp };

const ANTIMICROBIANOS: Antimicrobiano[] = [...LISTA_IN360, ...ALIASES].map(
  (linha) => {
    const [nome, radical] = linha.split("|");
    // Início de palavra: evita que "Ciprofloxacino" case com o radical "ofloxacin"
    // e que "Benzilpenicilina" case com "penicilina" (esse tem alias próprio).
    return {
      nome,
      re: new RegExp(`(?:^|[^a-z0-9])${escaparRegex(normalizar(radical ?? nome))}`),
    };
  }
);

/** Quantas substâncias vieram da IN 360 (trava de regressão nos testes). */
export const TOTAL_IN360 = LISTA_IN360.length;

/**
 * Verifica se o nome do medicamento contém um antimicrobiano da lista.
 * Retorna o princípio ativo encontrado ou null.
 */
export function detectarAntimicrobiano(nomeMedicamento: string): string | null {
  const nomeNorm = normalizar(nomeMedicamento);
  for (const item of ANTIMICROBIANOS) {
    if (item.re.test(nomeNorm)) {
      return item.nome;
    }
  }
  return null;
}

/**
 * Verifica uma lista de medicamentos e retorna quais são antimicrobianos
 * (princípios ativos, sem repetição).
 */
export function detectarAntimicrobianos(
  medicamentos: { nome: string }[]
): string[] {
  const encontrados: string[] = [];
  for (const med of medicamentos) {
    const ativo = detectarAntimicrobiano(med.nome);
    if (ativo && !encontrados.includes(ativo)) {
      encontrados.push(ativo);
    }
  }
  return encontrados;
}
